/**
 * Sentar (Renato, 04/11/2026): como o cliente da UO senta o personagem numa cadeira, num banco ou num trono.
 *
 * A UO não tem animação de sentar para quem olha para a câmera. O cliente (o ClassicUO segue o original:
 * MobileView.cs, AnimationsLoader.FixSittingDirection e Batcher2D.DrawCharacterSitted) faz assim:
 * - quem pisa num item da ChairTable senta nele, virado para uma das direções que o assento aceita;
 * - de frente (S e L): o quadro parado de pé, desenhado em três faixas. O tronco desliza 8 px para o encosto, a coxa
 *   vira um paralelogramo (do tronco deslocado ao joelho no lugar) e a canela encolhe: parece sentado;
 * - de costas (N e O): a pose do cavaleiro parado (grupo 25), sem o cavalo — as pernas abertas viram o assento.
 * Os números (as razões 0,35 / 0,60 / 0,94, os 8 px e os deslocamentos em y) são os do cliente.
 */

/** As direções do assento (UO: 0 N, 2 L, 4 S, 6 O; -1 = vale a vizinha) e os deslocamentos de frente e de costas. */
export type DadosAssento = readonly number[];

export interface PoseSentada {
  /** A direção do jogo (0 L … 7 NE) em que o corpo é desenhado. */
  readonly dir: number;
  /** De costas para a câmera: a pose do cavaleiro sem o cavalo. De frente: o quadro de pé em três faixas. */
  readonly costas: boolean;
  /** O deslocamento da âncora, em px da arte (vezes o zoom na tela). */
  readonly dx: number;
  readonly dy: number;
  /** O quanto o tronco desliza para o encosto, em px da arte (só de frente). */
  readonly encosto: number;
}

/** O que o cliente soma em y a todo personagem sentado. */
const SIT_OFFSET_Y = 4;
/** O deslize do tronco para o encosto. */
export const DESLIZE_TRONCO = 8;
/** Onde o quadro de pé é cortado: cintura, joelho e pé (a fração da altura do corpo). */
export const CINTURA = 0.35;
export const JOELHO = 0.6;
export const PE = 0.94;

/** A direção da UO (0 N … 7 NO) a partir da do jogo (0 L … 7 NE) e de volta. */
const uoDe = (dirJogo: number) => (((dirJogo + 2) % 8) + 8) % 8;
const jogoDe = (dirUo: number) => (dirUo + 6) % 8;

/** A direção da UO em que o personagem senta: a mais perto da dele que o assento aceita (FixSittingDirection). */
export function direcaoNoAssento(a: DadosAssento, dirUo: number): number {
  const [d1, d2, d3, d4] = a;
  switch (dirUo & 7) {
    case 7: case 0: return d1 !== -1 ? d1 : (dirUo === 7 ? d4 : d2);
    case 1: case 2: return d2 !== -1 ? d2 : (dirUo === 1 ? d1 : d3);
    case 3: case 4: return d3 !== -1 ? d3 : (dirUo === 3 ? d2 : d4);
    default: return d4 !== -1 ? d4 : (dirUo === 5 ? d3 : d1);
  }
}

/** Como desenhar quem está sentado em `a`, olhando para `dirJogo`. `null` se os dados do assento não servem. */
export function poseSentada(a: DadosAssento | null | undefined, dirJogo: number): PoseSentada | null {
  if (!a || a.length < 6 || !a.every(Number.isFinite)) return null;
  const uo = direcaoNoAssento(a, uoDe(dirJogo));
  const off = a[4], offCostas = a[5];
  switch (uo) {
    // De costas: N (espelhado) e O.
    case 0: return { dir: jogoDe(0), costas: true, dx: DESLIZE_TRONCO - 4, dy: 25 + offCostas + SIT_OFFSET_Y, encosto: 0 };
    case 6: return { dir: jogoDe(6), costas: true, dx: -3, dy: 23 + offCostas + SIT_OFFSET_Y, encosto: 0 };
    // De frente: L (espelhado, o encosto fica à esquerda) e S.
    case 2: return { dir: jogoDe(2), costas: false, dx: 0, dy: off + 9 + SIT_OFFSET_Y, encosto: -DESLIZE_TRONCO };
    case 4: return { dir: jogoDe(4), costas: false, dx: -(DESLIZE_TRONCO + 1), dy: 10 + off + SIT_OFFSET_Y, encosto: DESLIZE_TRONCO };
    default: return null;
  }
}

/**
 * As três faixas do corpo de frente (Batcher2D.DrawCharacterSitted), com `topo` e `altura` do corpo na imagem de origem:
 * de onde cada faixa sai (y0, h na origem) e para onde vai (y, h no destino), e o deslize no alto e no pé da faixa.
 */
export interface FaixaSentada {
  readonly srcY: number;
  readonly srcH: number;
  readonly dstY: number;
  readonly dstH: number;
  readonly desliza0: number;
  readonly desliza1: number;
}

export function faixasSentado(topo: number, altura: number, encosto: number): readonly FaixaSentada[] {
  const h03 = altura * CINTURA, h06 = altura * JOELHO, h09 = altura * PE;
  return [
    // Do alto da imagem (o que estiver acima da cabeça vai junto) até a cintura: só desliza.
    { srcY: 0, srcH: topo + h03, dstY: 0, dstH: topo + h03, desliza0: encosto, desliza1: encosto },
    // A coxa: da cintura até 60% do que sobra, apertada até o joelho, inclinada do encosto para o lugar.
    { srcY: topo + h03, srcH: (altura - h03) * JOELHO, dstY: topo + h03, dstH: h06 - h03, desliza0: encosto, desliza1: 0 },
    // A canela: do joelho até 94% do que sobra, apertada até o pé.
    { srcY: topo + h06, srcH: (altura - h06) * PE, dstY: topo + h06, dstH: h09 - h06, desliza0: 0, desliza1: 0 }
  ];
}
